import type {
  AssociatedRegistryType,
  EntityAssociateType,
  FieldType,
  RegistryType,
  RegistryFieldValueType,
} from "@/schemas/digital-files";
import {
  compareIdArrays,
  findInitialDataCenterOptionByName,
  findInitialFieldValueByName,
  getFieldDefaultValue,
  normalizeValueForComparison,
} from "./utils";

type FieldOption =
  string | { id: string; table: string; display: string; source: string };

export interface ChangeDetectionParams {
  regName: string;
  users: { applicant_id: string }[];
  registries: { id: string }[];
  fieldValues: Record<string, unknown>;
  fieldOptions: Record<string, FieldOption>;
}

export interface EditChangeDetectionParams extends ChangeDetectionParams {
  registryData?: RegistryType;
}

function parseFieldValue(fieldValue: RegistryFieldValueType): unknown {
  let parsedValue: unknown = fieldValue.value;
  const fieldType = fieldValue.entity_field?.field_type;

  if (
    (fieldValue.entity_field?.is_array || fieldType === "MULTISELECT") &&
    typeof parsedValue === "string" &&
    parsedValue
  ) {
    try {
      parsedValue = JSON.parse(parsedValue);
    } catch {
      parsedValue = [parsedValue];
    }
  }

  if (fieldType === "URL") {
    parsedValue =
      fieldValue.files?.map((file) => ({ ...file, name: file.file_name })) ||
      [];
  }

  return parsedValue;
}

function findDataCenterRelation(
  registryData: RegistryType,
  fieldValue: RegistryFieldValueType,
  fieldType: string,
): { value: unknown; found: boolean } {
  const dbRelations = registryData.data_center_relations || [];

  if (fieldType === "SELECT") {
    const match = dbRelations.find(
      (rel) =>
        rel.expedient_base_entity_field_id ===
          fieldValue.expedient_base_entity_field_id &&
        rel.relation_type === "field_value",
    );
    if (match) return { value: match.relation_id, found: true };
  }

  if (fieldType === "MULTISELECT") {
    const matches = dbRelations.filter(
      (rel) =>
        rel.expedient_base_entity_field_id ===
          fieldValue.expedient_base_entity_field_id &&
        rel.relation_type === "field_value",
    );
    if (matches.length > 0) {
      return { value: matches.map((rel) => rel.relation_id), found: true };
    }
  }

  return { value: null, found: false };
}

export function buildFieldValuesFromRegistry(
  registryData: RegistryType,
  fields?: FieldType[],
): Record<string, unknown> {
  if (!registryData.field_values?.length) return {};

  return registryData.field_values.reduce<Record<string, unknown>>(
    (acc, fieldValue) => {
      const currentFieldId = fields?.find(
        (f) =>
          f.name === fieldValue.entity_field?.name &&
          f.field_type === fieldValue.entity_field?.field_type,
      )?.id;
      if (!currentFieldId || !fieldValue.entity_field?.id) return acc;

      const dcRelation = findDataCenterRelation(
        registryData,
        fieldValue,
        fieldValue.entity_field.field_type,
      );

      acc[currentFieldId] = dcRelation.found
        ? dcRelation.value
        : parseFieldValue(fieldValue);

      return acc;
    },
    {},
  );
}

// Función para transformar fieldOptions al formato de API
export const transformFieldOptionsForAPI = (
  options: Record<
    string,
    string | { id: string; table: string; display: string; source: string }
  >,
) => {
  const transformed: Record<string, unknown> = {};

  for (const [fieldId, option] of Object.entries(options)) {
    transformed[fieldId] = option;
  }

  return transformed;
};

export function computeCreateModeChanges(
  fields: FieldType[],
  {
    regName,
    users,
    registries,
    fieldValues,
    fieldOptions,
  }: ChangeDetectionParams,
): boolean {
  if (regName.trim() !== "") return true;
  if (users.length > 0) return true;
  if (registries.length > 0) return true;

  for (const field of fields) {
    const currentVal = fieldValues[field.id];
    const defaultVal = getFieldDefaultValue(field);
    if (
      normalizeValueForComparison(currentVal) !==
      normalizeValueForComparison(defaultVal)
    )
      return true;

    const currentOpt = fieldOptions[field.id];
    if (currentOpt && normalizeValueForComparison(currentOpt) !== "")
      return true;
  }
  return false;
}

export function computeEditModeChanges(
  fields: FieldType[],
  {
    regName,
    users,
    registries,
    fieldValues,
    fieldOptions,
    registryData,
  }: EditChangeDetectionParams,
): boolean {
  const initialName = registryData?.name || "";
  if (
    normalizeValueForComparison(regName) !==
    normalizeValueForComparison(initialName)
  )
    return true;

  const initialUsersIds = (registryData?.associates || []).map(
    (a: EntityAssociateType) => a.applicant_id,
  );
  if (
    !compareIdArrays(
      initialUsersIds,
      users.map((u) => u.applicant_id),
    )
  )
    return true;

  const initialRegIds = (registryData?.related_registries || []).map(
    (r: AssociatedRegistryType) => r.id,
  );
  if (
    !compareIdArrays(
      initialRegIds,
      registries.map((r) => r.id),
    )
  )
    return true;

  for (const field of fields) {
    const currentVal = fieldValues[field.id];
    const initialVal = findInitialFieldValueByName(
      field.name,
      field.field_type,
      registryData,
    );
    if (
      normalizeValueForComparison(currentVal) !==
      normalizeValueForComparison(initialVal)
    )
      return true;

    const currentOpt = fieldOptions[field.id];
    const currentOptId =
      typeof currentOpt === "string" ? currentOpt : currentOpt?.id || "";
    const initialOptId = findInitialDataCenterOptionByName(
      field.name,
      registryData,
    );
    if (
      normalizeValueForComparison(currentOptId) !==
      normalizeValueForComparison(initialOptId)
    )
      return true;
  }
  return false;
}
